import { useSyncExternalStore } from "react";
import { safeLocalStorage } from "@/lib/storage";

export type ExperimentToggle = "off" | "on";

export interface ChatExperiments {
  taskSections: ExperimentToggle;
  headerAttachments: ExperimentToggle;
  summaryPanel: ExperimentToggle;
  workspacePane: ExperimentToggle;
}

export type ChatExperimentKey = keyof ChatExperiments;

const STORAGE_KEY = "codesmith-chat-experiments";
const DEFAULTS: ChatExperiments = {
  taskSections: "off",
  headerAttachments: "off",
  summaryPanel: "on",
  workspacePane: "on",
};
const listeners = new Set<() => void>();

let snapshot: ChatExperiments | null = null;

function readStorage(): ChatExperiments {
  const raw = safeLocalStorage.get(STORAGE_KEY);
  if (!raw) return DEFAULTS;
  try {
    const stored = JSON.parse(raw) as Partial<
      Record<keyof ChatExperiments, unknown>
    >;
    const keys = Object.keys(DEFAULTS) as ChatExperimentKey[];
    return Object.fromEntries(
      keys.map((k) => [
        k,
        stored[k] === "on" || stored[k] === "off" ? stored[k] : DEFAULTS[k],
      ]),
    ) as unknown as ChatExperiments;
  } catch {
    return DEFAULTS;
  }
}

function getSnapshot(): ChatExperiments {
  if (snapshot === null) snapshot = readStorage();
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function experimentDefault<K extends keyof ChatExperiments>(
  key: K,
): ChatExperiments[K] {
  return DEFAULTS[key];
}

export function setExperiment<K extends keyof ChatExperiments>(
  key: K,
  value: ChatExperiments[K],
): void {
  snapshot = { ...getSnapshot(), [key]: value };
  safeLocalStorage.set(STORAGE_KEY, JSON.stringify(snapshot));
  for (const listener of listeners) listener();
}

export function useExperiment<K extends keyof ChatExperiments>(
  key: K,
): ChatExperiments[K] {
  return useSyncExternalStore(
    subscribe,
    () => getSnapshot()[key],
    () => DEFAULTS[key],
  );
}
